import Api from "@/services/Api";
import { useEffect, useState, useCallback, useRef } from "react";

interface IDBItem {
  userId: number;
  schemaId: number;
  ownerId: number;
  content: any;
  instanceId: number;
  documentId: string;
}
interface IDBItemWithID extends IDBItem {
  id: string;
  savedAt: Date;
}

export function createCompositeId(
  documentId: string,
  userId: number,
  schemaId: number,
  ownerId: number,
  instanceId: number
): string {
  return `${documentId}_${userId}_${schemaId}_${ownerId}_${instanceId}`;
}

export function useIndexedDB(
  dbName = "tempStorage",
  storeName = "documents"
): [
  IDBDatabase | null, //Database
  (item: IDBItem) => void, //AddItem
  (
    documentId: string,
    userId: number,
    schemaId: number,
    ownerId: number,
    instanceId: number,
    updatedAt?: string
  ) => Promise<IDBItemWithID | null>, //GetItem
  (documentId: string, userId: number, schemaId: number, ownerId: number, instanceId: number) => Promise<void> //DeleteItem
] {
  const [db, setDb] = useState<IDBDatabase | null>(null);
  const dbRef = useRef<IDBDatabase | null>(null);

  useEffect(() => {
    const openRequest = indexedDB.open(dbName, 1);

    openRequest.onupgradeneeded = function (event) {
      const database = (event.target as IDBOpenDBRequest).result;
      let store;
      if (!database.objectStoreNames.contains(storeName)) {
        store = database.createObjectStore(storeName, { keyPath: "id" });
      } else {
        store = (event?.target as IDBOpenDBRequest)?.transaction?.objectStore(storeName);
      }
    };
    openRequest.onsuccess = function (event) {
      const database = (event.target as IDBOpenDBRequest).result;
      setDb(database);

      // Cleanup old documents
      const transaction = database.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const now = Date.now();
      const threeMonths = 90 * 24 * 60 * 60 * 1000;

      // Collect valid (not expired) docs per user
      const userDocsMap: Record<number, IDBItemWithID[]> = {};
      const request = store.openCursor();
      request.onsuccess = function (event) {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const value = cursor.value as IDBItemWithID;
          // Delete if older than 3 months
          if (value.savedAt && now - new Date(value.savedAt).getTime() > threeMonths) {
            cursor.delete();
          } else {
            // Collect per user
            if (!userDocsMap[value.userId]) userDocsMap[value.userId] = [];
            userDocsMap[value.userId].push(value);
          }
          cursor.continue();
        } else {
          // After collecting, enforce max 3 per user
          Object.values(userDocsMap).forEach((docs) => {
            if (docs.length > 3) {
              docs.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime()); // newest first
              const toDelete = docs.slice(3); // keep 3 newest
              toDelete.forEach((doc) => store.delete(doc.id));
            }
          });
        }
      };
    };
    openRequest.onerror = function () {
      console.error("Error", openRequest.error);
    };
  }, [dbName, storeName]);

  useEffect(() => {
    dbRef.current = db;
  }, [db]);

  const addDocumentToIndexedDB = useCallback(
    (item: IDBItem) => {
      const dbCurrent = dbRef.current;
      if (!dbCurrent) return;

      const transaction = dbCurrent.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const compositeId = createCompositeId(item.documentId, item.userId, item.schemaId, item.ownerId, item.instanceId);
      store.put({ id: compositeId, savedAt: new Date().toISOString(), ...item });
    },
    [db, storeName]
  );

  const getDocumentFromIndexedDB = useCallback(
    (documentId: string, userId: number, schemaId: number, ownerId: number, instanceId: number, updatedAt?: string) =>
      new Promise<IDBItemWithID | null>((resolve, reject) => {
        const dbCurrent = dbRef.current;
        if (!dbCurrent) return;

        const compositeId = createCompositeId(documentId, userId, schemaId, ownerId, instanceId);
        const transaction = dbCurrent.transaction([storeName], "readonly");
        const store = transaction.objectStore(storeName);
        const req = store.get(compositeId);

        req.onsuccess = async () => {
          const currentItem = req.result as IDBItemWithID | null;
          if (!currentItem) {
            resolve(null);
            return;
          }

          //If the item in the DB is newer than the item in IndexDB, delete it and return null
          if (updatedAt && new Date(updatedAt) > new Date(currentItem.savedAt)) {
            deleteDocumentFromIndexedDB(documentId, userId, schemaId, ownerId, instanceId);
            resolve(null);
            return;
          }

          // If currentItem.content is not a valid schema, delete it and return null
          try {
            const [_, error, source] = await Api.validateDocument(currentItem.schemaId, currentItem.content).fetch();
            if ((source instanceof Response && source.status !== 200) || !!error) {
              await deleteDocumentFromIndexedDB(documentId, userId, schemaId, ownerId, instanceId);
              resolve(null);
              return;
            }
          } catch (error) {
            deleteDocumentFromIndexedDB(documentId, userId, schemaId, ownerId, instanceId);
            resolve(null);
            return;
          }

          resolve(currentItem);
        };
        req.onerror = () => reject(req.error);
      }),
    [db, storeName]
  );

  const deleteDocumentFromIndexedDB = useCallback(
    (documentId: string, userId: number, schemaId: number, ownerId: number, instanceId: number) =>
      new Promise<void>((resolve, reject) => {
        const dbCurrent = dbRef.current;
        if (!dbCurrent) return;

        const compositeId = createCompositeId(documentId, userId, schemaId, ownerId, instanceId);
        const transaction = dbCurrent.transaction([storeName], "readwrite");
        const store = transaction.objectStore(storeName);
        const req = store.delete(compositeId);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      }),
    [db, storeName]
  );

  return [db, addDocumentToIndexedDB, getDocumentFromIndexedDB, deleteDocumentFromIndexedDB];
}
